import { useEffect, useRef, useState, useCallback } from "react";
import { X, CaretLeft, CaretRight, Microphone, Stop, Pencil, TextT, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import PhotoMarkupModal from "@/components/PhotoMarkupModal";
import OfflineQueuePill from "@/components/OfflineQueuePill";
import { tryLiveOrQueue, makeVoiceNoteUpload, makeCaptionUpdate } from "@/lib/offlineQueue";

/**
 * WorkerPhotoLightbox — full-screen photo view for techs on WorkerJobViewV4.
 *
 * Closes the CompanyCam-parity loop on the worker side: tap any photo →
 * full-screen preview → markup / caption / hold-or-tap to record a
 * voice memo pinned to that specific photo. Playback of any existing
 * voice note also lives here.
 *
 * Voice note UX (per user preference — "both"):
 *   • Hold the button = press-and-hold recording; release to save
 *   • Tap the button   = toggle start/stop
 *   • Auto-stops at 15s (hard cap enforced by backend anyway)
 *   • Overwriting an existing voice note is allowed, confirmed inline
 *
 * Props:
 *   photos    — array of normalized photo entries { file_id, path, caption,
 *               caption_by, voice_note, kind, at }
 *   startIdx  — starting index in the photos array
 *   jobId     — parent WO id (needed for caption + voice + markup API calls)
 *   canEdit   — bool; false means view-only (admin peek / completed jobs)
 *   apiBase   — REACT_APP_BACKEND_URL to build absolute audio urls
 *   onClose() — dismiss handler
 *   onMutated(updatedPhoto) — parent should reload after a mutation
 */
export default function WorkerPhotoLightbox({
  photos, startIdx = 0, jobId, canEdit = true, apiBase = "", onClose, onMutated,
}) {
  const [idx, setIdx] = useState(startIdx);
  const total = photos?.length || 0;

  const [markupOpen, setMarkupOpen] = useState(false);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [captionDraft, setCaptionDraft] = useState("");
  const [savingCap, setSavingCap] = useState(false);

  // Recorder state
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [holdStartedAt, setHoldStartedAt] = useState(null); // ms — if null, tap-toggle mode
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickerRef = useRef(null);
  const autoStopRef = useRef(null);

  const p = photos?.[idx];
  const displayUrl = p ? (p.url || fileUrl(p.path)) : "";
  const voice = p?.voice_note;
  const authToken = typeof window !== "undefined" ? (localStorage.getItem("access_token") || "") : "";
  const voiceUrl = voice?.path
    ? `${apiBase}/api/files/${voice.path}?auth=${encodeURIComponent(authToken)}`
    : null;

  // Load caption draft when photo changes
  useEffect(() => {
    setCaptionDraft(p?.caption || "");
    setCaptionOpen(false);
  }, [p?.file_id]);

  // Keyboard nav
  const goPrev = useCallback(() => setIdx(i => (i - 1 + total) % total), [total]);
  const goNext = useCallback(() => setIdx(i => (i + 1) % total), [total]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext]);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Swipe nav
  const touchStart = useRef({ x: 0, y: 0 });
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchStart.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStart.current.x;
    const dy = t.clientY - touchStart.current.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) goPrev(); else goNext();
    }
  };

  // ── Recorder helpers ─────────────────────────────────────────────
  const cleanupRecorder = () => {
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach(t => t.stop()); } catch {}
      streamRef.current = null;
    }
    mediaRecRef.current = null;
    chunksRef.current = [];
  };

  const startRecording = async () => {
    if (recording) return;
    if (voice && !window.confirm("Replace the existing voice note on this photo?")) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        cleanupRecorder();
        setRecording(false);
        setElapsed(0);
        if (!blob || blob.size < 500) {
          toast.error("Recording too short — try again");
          return;
        }
        await uploadVoiceNote(blob);
      };
      rec.start();
      mediaRecRef.current = rec;
      setRecording(true);
      setElapsed(0);
      tickerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      // Hard cap at 15s
      autoStopRef.current = setTimeout(() => {
        try { rec.state !== "inactive" && rec.stop(); } catch {}
      }, 15_000);
    } catch (e) {
      cleanupRecorder();
      setRecording(false);
      toast.error(e?.name === "NotAllowedError"
        ? "Mic permission denied — enable it in your browser settings"
        : "Couldn't start recording");
    }
  };

  const stopRecording = () => {
    const rec = mediaRecRef.current;
    if (!rec) return;
    try { rec.state !== "inactive" && rec.stop(); } catch {}
    // onstop handler handles the rest
  };

  const uploadVoiceNote = async (blob) => {
    if (!p?.file_id) { toast.error("Photo id missing — can't attach"); return; }
    const token = localStorage.getItem("access_token") || "";
    try {
      const { queued } = await tryLiveOrQueue(
        ({ token }) => makeVoiceNoteUpload({ jobId, fileId: p.file_id, blob, token }),
        token,
      );
      if (queued) {
        toast("📶 Offline — voice note queued");
      } else {
        toast.success("Voice note saved");
        onMutated?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Voice note upload failed");
    }
  };

  const deleteVoiceNote = async () => {
    if (!voice || !p?.file_id) return;
    if (!window.confirm("Delete this voice note?")) return;
    try {
      await api.delete(`/jobs/${jobId}/photo/${p.file_id}/voice-note`);
      toast.success("Voice note deleted");
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // ── Hold-vs-tap dispatcher ────────────────────────────────────────
  // Hold: onPointerDown starts, onPointerUp stops IF held ≥ 400ms
  // Tap:  onPointerUp within < 400ms toggles start/stop (like a click)
  const HOLD_THRESHOLD_MS = 400;
  const onMicPointerDown = (e) => {
    e.preventDefault();
    if (recording) {
      // If already recording (from a previous tap-start), a second press stops.
      stopRecording();
      return;
    }
    setHoldStartedAt(Date.now());
    startRecording();
  };
  const onMicPointerUp = (e) => {
    e.preventDefault();
    const start = holdStartedAt;
    setHoldStartedAt(null);
    if (!recording) return;
    const held = start ? Date.now() - start : 0;
    if (held >= HOLD_THRESHOLD_MS) {
      // Treat as hold-to-record: stop on release
      stopRecording();
    }
    // else: quick tap → keep recording (tap-toggle mode). User taps again to stop.
  };

  // ── Caption save ─────────────────────────────────────────────────
  const saveCaption = async () => {
    if (!p?.file_id) return;
    setSavingCap(true);
    const token = localStorage.getItem("access_token") || "";
    try {
      const { queued } = await tryLiveOrQueue(
        ({ token }) => makeCaptionUpdate({ jobId, fileId: p.file_id, caption: captionDraft, token }),
        token,
      );
      if (queued) {
        toast("📶 Offline — caption queued");
      } else {
        toast.success("Caption saved");
      }
      setCaptionOpen(false);
      onMutated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSavingCap(false);
    }
  };

  if (!p) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex flex-col select-none"
      data-testid="wjv-lightbox"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 text-white/90">
        <button
          onClick={onClose}
          data-testid="wjv-lightbox-close"
          className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-white/10"
          aria-label="Close"
        >
          <X size={22} weight="bold" />
        </button>
        <div className="text-[10px] font-black uppercase tracking-widest text-white/70 flex items-center gap-2">
          {p.kind ? <span className="text-white/60">{p.kind} · </span> : null}
          {idx + 1} / {total}
          <OfflineQueuePill compact />
        </div>
        <div className="w-10" />
      </div>

      {/* Stage */}
      <div
        className="flex-1 relative flex items-center justify-center px-3 pb-3"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {total > 1 && (
          <button
            onClick={goPrev}
            data-testid="wjv-lightbox-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center bg-white/15 hover:bg-white/30 text-white rounded-full shadow-lg"
            aria-label="Previous"
          >
            <CaretLeft size={26} weight="bold" />
          </button>
        )}
        <img
          key={idx}
          src={displayUrl}
          alt=""
          data-testid="wjv-lightbox-image"
          className="max-h-full max-w-full object-contain shadow-2xl"
          draggable={false}
        />
        {total > 1 && (
          <button
            onClick={goNext}
            data-testid="wjv-lightbox-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-12 w-12 flex items-center justify-center bg-white/15 hover:bg-white/30 text-white rounded-full shadow-lg"
            aria-label="Next"
          >
            <CaretRight size={26} weight="bold" />
          </button>
        )}
      </div>

      {/* Caption strip (read-only glance) */}
      {p.caption && !captionOpen && (
        <div className="px-4 py-2 text-center text-white/90 text-sm font-medium bg-black/70" data-testid="wjv-lightbox-caption-preview">
          {p.caption}
          {p.caption_by ? <span className="text-white/50 text-[11px] font-bold ml-2">— {p.caption_by}</span> : null}
        </div>
      )}

      {/* Existing voice note playback */}
      {voice && voiceUrl && (
        <div className="mx-3 mb-2 rounded-xl bg-purple-500/15 border border-purple-500/40 p-2.5" data-testid="wjv-lightbox-voice-row">
          <div className="text-[9px] font-black uppercase tracking-widest text-purple-200 mb-1.5 flex items-center gap-2">
            🎙️ Voice Note {voice.by ? `· ${voice.by}` : ""}
            {canEdit && (
              <button onClick={deleteVoiceNote}
                data-testid="wjv-lightbox-voice-delete"
                className="ml-auto text-purple-200/70 hover:text-red-300 inline-flex items-center gap-1">
                <Trash size={12} weight="bold" /> Delete
              </button>
            )}
          </div>
          <audio controls preload="metadata" data-testid="wjv-lightbox-voice-audio" src={voiceUrl} className="w-full h-9"/>
        </div>
      )}

      {/* Caption editor sheet */}
      {captionOpen && canEdit && (
        <div className="mx-3 mb-2 rounded-xl bg-white/10 border border-white/20 p-3" data-testid="wjv-lightbox-caption-editor">
          <div className="text-[10px] font-black uppercase tracking-widest text-white/60 mb-1.5">Caption</div>
          <textarea
            value={captionDraft}
            onChange={(e) => setCaptionDraft(e.target.value.slice(0, 500))}
            placeholder="e.g. 'shut-off valve, kitchen sink'"
            data-testid="wjv-lightbox-caption-input"
            rows={2}
            className="w-full bg-white/10 text-white placeholder-white/40 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00C4CC] resize-none"/>
          <div className="flex items-center gap-2 mt-2">
            <div className="text-[10px] text-white/40 font-bold">{captionDraft.length}/500</div>
            <button onClick={() => { setCaptionDraft(p.caption || ""); setCaptionOpen(false); }}
              data-testid="wjv-lightbox-caption-cancel"
              className="ml-auto h-8 px-3 rounded-md bg-white/10 hover:bg-white/20 text-white font-black uppercase tracking-widest text-[10px]">
              Cancel
            </button>
            <button onClick={saveCaption} disabled={savingCap}
              data-testid="wjv-lightbox-caption-save"
              className="h-8 px-3 rounded-md bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-60 text-[#0F1F3A] font-black uppercase tracking-widest text-[10px]">
              {savingCap ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* Bottom action bar */}
      {canEdit && p.file_id && (
        <div className="border-t border-white/10 bg-black/80 px-3 py-2.5 flex items-center gap-2" data-testid="wjv-lightbox-actions">
          <button
            onClick={() => setMarkupOpen(true)}
            data-testid="wjv-lightbox-markup"
            className="h-11 flex-1 rounded-lg bg-white/10 hover:bg-white/20 text-white font-black uppercase tracking-widest text-[11px] inline-flex items-center justify-center gap-1.5"
          >
            <Pencil size={16} weight="bold" /> Markup
          </button>
          <button
            onClick={() => setCaptionOpen((v) => !v)}
            data-testid="wjv-lightbox-caption-toggle"
            className={`h-11 flex-1 rounded-lg font-black uppercase tracking-widest text-[11px] inline-flex items-center justify-center gap-1.5 ${
              captionOpen ? "bg-[#00C4CC] text-[#0F1F3A]" : "bg-white/10 hover:bg-white/20 text-white"
            }`}
          >
            <TextT size={16} weight="bold" /> Caption
          </button>
          {/* Hold-or-tap voice recorder */}
          <button
            onPointerDown={onMicPointerDown}
            onPointerUp={onMicPointerUp}
            onPointerLeave={(e) => { if (recording) onMicPointerUp(e); }}
            onContextMenu={(e) => e.preventDefault()}
            data-testid="wjv-lightbox-voice-record"
            className={`h-11 flex-1 rounded-lg font-black uppercase tracking-widest text-[11px] inline-flex items-center justify-center gap-1.5 touch-none ${
              recording ? "bg-red-600 text-white animate-pulse" : "bg-[#00C4CC] hover:bg-[#00e5ed] text-[#0F1F3A]"
            }`}
          >
            {recording ? (
              <>
                <Stop size={16} weight="fill" /> {String(elapsed).padStart(2, "0")}s
              </>
            ) : (
              <>
                <Microphone size={16} weight="bold" /> {voice ? "Replace" : "Voice"}
              </>
            )}
          </button>
        </div>
      )}
      {canEdit && p.file_id && (
        <div className="text-[9px] font-black uppercase tracking-widest text-white/40 text-center pb-2" data-testid="wjv-lightbox-voice-hint">
          Hold to record · Tap to toggle · Max 15s
        </div>
      )}

      {/* Markup modal (reuses admin component) */}
      {markupOpen && (
        <PhotoMarkupModal
          src={displayUrl}
          jobId={jobId}
          onClose={() => setMarkupOpen(false)}
          onSaved={() => { setMarkupOpen(false); onMutated?.(); }}
        />
      )}
    </div>
  );
}
